import { useEffect, useMemo, useRef, useState } from "react";
import { searchListingsForPicker } from "../listingsMatch/algoliaClient";
import { useAlgoliaIndex } from "contexts/AlgoliaClientContext";
import { isPgSearchEnabled } from "utils/listingsSearchApi";
import { mergePickerResults, shouldSearchMarket } from "./pickerListingSearch";
import type { ListingListedFor, ProcessedListingData } from "../../../@types/sharedSchema";

type Hit = ProcessedListingData & { objectID?: string };

/**
 * "בחר נכס"'s search, for both pickers (Trello #254). See `pickerListingSearch.ts` for what is asked.
 *
 * ── The newest term always wins ─────────────────────────────────────────────────────
 *
 * Both pickers used to SKIP a search while one was in flight (`if (loadingSearch) return` /
 * `if (fetchingRef.current) return`) and never came back to it: type on while a search ran and
 * the last thing typed was never searched, so the list kept showing the answer to an older,
 * shorter term — "you have to type it exactly". Here every term is asked, and only the answer to
 * the NEWEST one is ever shown.
 *
 * ── Her own listings do not wait for the market ─────────────────────────────────────
 *
 * The two queries are independent; her own listings (few, owner-scoped, fast) are shown as soon
 * as they arrive, and the market's are added when they do.
 */
export default function usePickerListingSearch(
  term: string,
  options: { listedFor?: ListingListedFor; enabled?: boolean } = {}
): { listings: ProcessedListingData[]; loading: boolean } {
  const algoliaIndex = useAlgoliaIndex();
  const { listedFor, enabled = true } = options;
  const canSearch = enabled && (isPgSearchEnabled() || Boolean(algoliaIndex));

  const [own, setOwn] = useState<Hit[]>([]);
  const [market, setMarket] = useState<Hit[]>([]);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    if (!canSearch) return;
    const request = ++latest.current;
    const isLatest = () => request === latest.current;
    setLoading(true);

    // 30, not 10: the sale/rent split halves the budget per type, and at 10 a realtor with more
    // than five own sale listings lost the older ones from the dropdown entirely.
    const ownSearch = searchListingsForPicker(algoliaIndex, {
      searchTerm: term,
      listingListedFor: listedFor,
      onlyWebotOrigin: true,
      maxResults: 30
    }).then(
      (hits) => {
        if (isLatest()) setOwn(hits as Hit[]);
      },
      (error) => console.error("Error fetching own listings:", error)
    );

    const marketSearch = shouldSearchMarket(term)
      ? searchListingsForPicker(algoliaIndex, { searchTerm: term, listingListedFor: listedFor, maxResults: 30 }).then(
          (hits) => {
            if (isLatest()) setMarket(hits as Hit[]);
          },
          (error) => console.error("Error fetching listings:", error)
        )
      : Promise.resolve().then(() => {
          if (isLatest()) setMarket([]);
        });

    void Promise.all([ownSearch, marketSearch]).then(() => {
      if (isLatest()) setLoading(false);
    });
  }, [term, listedFor, canSearch, algoliaIndex]);

  const listings = useMemo(() => mergePickerResults(own, market), [own, market]);
  return { listings, loading };
}
